/**
 * SpecToTestModal — AI Spec-to-Test Generation Wizard
 *
 * 3-step UI:
 *  Step 1 — Upload:     File drag-drop + test style selector + "Analyze Document" trigger
 *  Step 2 — Processing: Animated 4-stage pipeline progress indicator (SSE-driven)
 *  Step 3 — Review:     Generated tests table grouped by suite + dropped tests + Import button
 */

import { useState, useCallback, useRef, Fragment } from 'react';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import axios from 'axios';
import {
    X, Upload, FileText, FileCode, CheckCircle2, Loader2,
    AlertCircle, ChevronDown, ChevronRight, Wand2, ShieldCheck,
    ClipboardList, Package, Download, AlertTriangle,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config/api';

// ── Constants ─────────────────────────────────────────────────────────────────

const ACCEPTED_MIME = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
    'text/markdown',
    'text/plain',
    'text/x-markdown',
];
const ACCEPTED_EXT = '.pdf,.docx,.doc,.md,.txt';
const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB

// ── Types ─────────────────────────────────────────────────────────────────────

type TestStyle = 'bdd' | 'procedural' | 'tdd';

interface IGeneratedTest {
    title: string;
    suite?: string;
    preconditions?: string;
    steps: Array<{ action: string; expectedResult: string }>;
    tags?: string[];
}

interface IDroppedTest {
    title: string;
    reason: string;
}

interface IPipelineStats {
    extracted: number;
    drafted: number;
    approved: number;
    duplicatesRemoved: number;
}

interface ICompletePayload {
    generated: IGeneratedTest[];
    dropped: IDroppedTest[];
    stats: IPipelineStats;
}

interface IPipelineStage {
    stage: number;
    label: string;
    count: number;
}

type ModalStep = 'upload' | 'processing' | 'review';

// ── Sub-components ────────────────────────────────────────────────────────────

const STYLE_OPTIONS: Array<{
    value: TestStyle;
    label: string;
    description: string;
    example: string;
}> = [
    {
        value: 'bdd',
        label: 'BDD',
        description: 'Given / When / Then',
        example: 'Given the user is logged in, When they click Checkout, Then the order is placed.',
    },
    {
        value: 'procedural',
        label: 'Procedural',
        description: 'Action / Expected Result',
        example: 'Click the Checkout button → Order confirmation page is displayed.',
    },
    {
        value: 'tdd',
        label: 'TDD',
        description: 'Arrange / Act / Assert',
        example: 'should redirect to dashboard after successful authentication',
    },
];

const PIPELINE_STAGES = [
    { stage: 1, icon: FileText, label: 'Extracting requirements' },
    { stage: 2, icon: Wand2, label: 'Generating test drafts' },
    { stage: 3, icon: ShieldCheck, label: 'Reviewing for accuracy' },
    { stage: 4, icon: Package, label: 'Formatting test cases' },
];

function getFileIcon(filename: string) {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return <FileText size={18} className="text-red-500" />;
    if (ext === 'docx' || ext === 'doc') return <FileText size={18} className="text-blue-500" />;
    return <FileCode size={18} className="text-slate-500" />;
}

function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// ── Main Component ────────────────────────────────────────────────────────────

export function SpecToTestModal({
    isOpen,
    projectId,
    onClose,
    onImported,
}: {
    isOpen: boolean;
    projectId: string;
    onClose: () => void;
    onImported: () => void;
}) {
    const { token } = useAuth();

    // ── Step state ───────────────────────────────────────────────────────────
    const [step, setStep] = useState<ModalStep>('upload');

    // Step 1 state
    const [file, setFile] = useState<File | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [fileError, setFileError] = useState<string | null>(null);
    const [testStyle, setTestStyle] = useState<TestStyle>('procedural');
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Step 2 state
    const [pipelineStages, setPipelineStages] = useState<IPipelineStage[]>([]);
    const [currentStage, setCurrentStage] = useState(0);
    const [pipelineError, setPipelineError] = useState<string | null>(null);

    // Step 3 state
    const [result, setResult] = useState<ICompletePayload | null>(null);
    const [expandedSuites, setExpandedSuites] = useState<Set<string>>(new Set());
    const [expandedTests, setExpandedTests] = useState<Set<string>>(new Set());
    const [showDropped, setShowDropped] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [importError, setImportError] = useState<string | null>(null);

    // ── Reset when modal opens/closes ────────────────────────────────────────
    const resetState = useCallback(() => {
        setStep('upload');
        setFile(null);
        setFileError(null);
        setTestStyle('procedural');
        setPipelineStages([]);
        setCurrentStage(0);
        setPipelineError(null);
        setResult(null);
        setExpandedSuites(new Set());
        setExpandedTests(new Set());
        setShowDropped(false);
        setIsImporting(false);
        setImportError(null);
        setIsDragging(false);
    }, []);

    const handleClose = useCallback(() => {
        if (step === 'processing') return;
        if (step === 'review') {
            if (!window.confirm('Are you sure you want to close? You will lose all generated tests.')) return;
        }
        resetState();
        onClose();
    }, [resetState, onClose, step]);

    // ── File handling ────────────────────────────────────────────────────────
    function validateAndSetFile(f: File) {
        setFileError(null);
        if (f.size > MAX_FILE_BYTES) {
            setFileError(`File is too large (${formatBytes(f.size)}). Maximum is 10 MB.`);
            return;
        }
        const ext = f.name.split('.').pop()?.toLowerCase();
        if (!['pdf', 'docx', 'doc', 'md', 'txt'].includes(ext ?? '')) {
            setFileError('Unsupported file type. Please upload a PDF, DOCX, or Markdown file.');
            return;
        }
        setFile(f);
    }

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        const dropped = e.dataTransfer.files[0];
        if (dropped) validateAndSetFile(dropped);
    }, []);

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => setIsDragging(false);

    const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selected = e.target.files?.[0];
        if (selected) validateAndSetFile(selected);
    };

    // ── SSE pipeline runner ───────────────────────────────────────────────────
    async function runPipeline() {
        if (!file || !token) return;

        setStep('processing');
        setPipelineStages([]);
        setCurrentStage(1);
        setPipelineError(null);

        const formData = new FormData();
        formData.append('file', file);
        formData.append('projectId', projectId);
        formData.append('testStyle', testStyle);

        let response: Response;
        try {
            response = await fetch(`${API_BASE_URL}/api/ai/spec-to-tests`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData,
            });
        } catch {
            setPipelineError('Network error — could not reach the server.');
            return;
        }

        if (!response.ok || !response.body) {
            const text = await response.text().catch(() => '');
            try {
                const json = JSON.parse(text);
                setPipelineError(json.error ?? 'Server error');
            } catch {
                setPipelineError(`Server returned ${response.status}`);
            }
            return;
        }

        // Parse SSE from ReadableStream (fetch-based, supports POST)
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        let gotCompleteEvent = false;
        let localError: string | null = null;

        const setError = (msg: string) => {
            localError = msg;
            setPipelineError(msg);
        };

        const handleEvent = (eventType: string, dataStr: string) => {
            try {
                const payload = JSON.parse(dataStr);
                if (eventType === 'progress') {
                    const stage = payload as IPipelineStage;
                    setCurrentStage(stage.stage);
                    setPipelineStages((prev) => {
                        const next = prev.filter((s) => s.stage !== stage.stage);
                        return [...next, stage].sort((a, b) => a.stage - b.stage);
                    });
                } else if (eventType === 'complete') {
                    gotCompleteEvent = true;
                    const complete = payload as ICompletePayload;
                    setResult(complete);
                    // Auto-expand all suites in review
                    const suites = new Set(
                        complete.generated.map((t) => t.suite ?? 'Uncategorized'),
                    );
                    setExpandedSuites(suites);
                    setStep('review');
                } else if (eventType === 'error') {
                    setError(payload.message ?? 'Pipeline failed');
                }
            } catch (err) {
                if (eventType === 'complete') {
                    console.error('[SpecToTestModal] Failed to parse complete event:', err, '\nRaw data:', dataStr);
                    setError('Failed to parse the final AI response. The payload might be too large.');
                }
            }
        };

        // currentEvent and currentData MUST live outside the while loop.
        // A large `complete` payload (≥ one TCP chunk) has its `event:` field in
        // chunk N and its `data:` field in chunk N+1. Declaring these inside the
        // loop resets them on every reader.read() call, causing the event type to
        // be lost and the complete event to dispatch silently as type 'message'.
        let currentEvent = 'message';
        let currentData = '';

        try {
            while (true) {
                const { done, value } = await reader.read();

                if (value) {
                    buffer += decoder.decode(value, { stream: !done });
                }

                const lines = buffer.split('\n');

                if (!done) {
                    // Keep the last (possibly incomplete) line in the buffer.
                    buffer = lines.pop() ?? '';
                } else {
                    // Stream closed — append a blank line so any buffered event
                    // that lacks a trailing \n\n still gets dispatched below.
                    lines.push('');
                    buffer = '';
                }

                for (const line of lines) {
                    if (line.startsWith('event: ')) {
                        currentEvent = line.slice(7).trim();
                    } else if (line.startsWith('data: ')) {
                        // Use slice(6) without .trim() to preserve JSON fidelity.
                        currentData += (currentData ? '\n' : '') + line.slice(6);
                    } else if (line === '' && currentData) {
                        handleEvent(currentEvent, currentData);
                        currentEvent = 'message';
                        currentData = '';
                    }
                }

                if (done) break;
            }
            // Safety net: stream closed without a complete or error event.
            // This also fires when a browser extension drops the close signal.
            if (!gotCompleteEvent && !localError) {
                setPipelineError('Connection lost before AI finished processing.');
            }
        } catch (err) {
            if (!localError) {
                setPipelineError('Stream interrupted unexpectedly.');
            }
        }
    }

    // ── Import ────────────────────────────────────────────────────────────────
    async function handleImport() {
        if (!result || isImporting) return;
        setIsImporting(true);
        setImportError(null);

        const CHUNK_SIZE = 50;
        const tests = result.generated.map((test) => ({
            ...test,
            steps: test.steps.map((step) => ({
                action: step.action || 'Perform action',
                expectedResult: step.expectedResult || 'Step executed successfully',
            })),
        }));

        try {
            for (let i = 0; i < tests.length; i += CHUNK_SIZE) {
                const chunk = tests.slice(i, i + CHUNK_SIZE);
                await axios.post(
                    `${API_BASE_URL}/api/test-cases/bulk`,
                    { projectId, testCases: chunk },
                    { headers: { Authorization: `Bearer ${token}` } },
                );
            }
            onImported();
            handleClose();
        } catch (err: any) {
            setImportError(err?.response?.data?.error ?? 'Import failed. Please try again.');
        } finally {
            setIsImporting(false);
        }
    }

    // ── Review helpers ────────────────────────────────────────────────────────
    const groupedTests = (): Record<string, IGeneratedTest[]> => {
        if (!result) return {};
        const groups: Record<string, IGeneratedTest[]> = {};
        for (const t of result.generated) {
            const suite = t.suite ?? 'Uncategorized';
            if (!groups[suite]) groups[suite] = [];
            groups[suite].push(t);
        }
        return groups;
    };

    const toggleSuite = (suite: string) =>
        setExpandedSuites((prev) => {
            const next = new Set(prev);
            next.has(suite) ? next.delete(suite) : next.add(suite);
            return next;
        });

    const toggleTest = (key: string) =>
        setExpandedTests((prev) => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <Transition show={isOpen} as={Fragment}>
            <Dialog onClose={handleClose} className="relative z-50">
                {/* Backdrop */}
                <TransitionChild
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" aria-hidden="true" />
                </TransitionChild>

                {/* Panel */}
                <div className="fixed inset-0 flex items-center justify-center p-4">
                    <TransitionChild
                        as={Fragment}
                        enter="ease-out duration-200"
                        enterFrom="opacity-0 scale-95 translate-y-2"
                        enterTo="opacity-100 scale-100 translate-y-0"
                        leave="ease-in duration-150"
                        leaveFrom="opacity-100 scale-100 translate-y-0"
                        leaveTo="opacity-0 scale-95 translate-y-2"
                    >
                        <DialogPanel className="w-full max-w-2xl max-h-[90vh] flex flex-col rounded-2xl bg-white dark:bg-gh-bg-dark shadow-2xl border border-slate-200 dark:border-gh-border-dark overflow-hidden">

                            {/* ── Modal Header ──────────────────────────────── */}
                            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-gh-border-dark shrink-0">
                                <div className="flex items-center gap-3">
                                    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-violet-50 dark:bg-violet-950/40">
                                        <Wand2 size={16} className="text-violet-600 dark:text-violet-400" />
                                    </div>
                                    <div>
                                        <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                                            Import from Spec
                                        </h2>
                                        <p className="text-xs text-slate-500 dark:text-slate-400">
                                            {step === 'upload' && 'Upload your specification document'}
                                            {step === 'processing' && 'AI is analysing your document…'}
                                            {step === 'review' && `${result?.generated.length ?? 0} tests ready to import`}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={handleClose}
                                    disabled={step === 'processing'}
                                    className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            {/* ── Step indicator ────────────────────────────── */}
                            <div className="flex items-center gap-0 px-6 py-3 border-b border-slate-100 dark:border-gh-border-dark bg-slate-50 dark:bg-gh-bg-subtle-dark shrink-0">
                                {(['upload', 'processing', 'review'] as ModalStep[]).map((s, idx) => (
                                    <Fragment key={s}>
                                        <div className={`flex items-center gap-1.5 text-xs font-medium ${step === s ? 'text-violet-600 dark:text-violet-400' : idx < ['upload', 'processing', 'review'].indexOf(step) ? 'text-green-600 dark:text-green-400' : 'text-slate-400 dark:text-slate-500'}`}>
                                            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${step === s ? 'bg-violet-600 text-white' : idx < ['upload', 'processing', 'review'].indexOf(step) ? 'bg-green-500 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-500'}`}>
                                                {idx < ['upload', 'processing', 'review'].indexOf(step) ? '✓' : idx + 1}
                                            </span>
                                            {s === 'upload' ? 'Upload' : s === 'processing' ? 'Analyze' : 'Review'}
                                        </div>
                                        {idx < 2 && <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700 mx-3" />}
                                    </Fragment>
                                ))}
                            </div>

                            {/* ── Modal Body ────────────────────────────────── */}
                            <div className="flex-1 overflow-y-auto">

                                {/* ── STEP 1: Upload ─────────────────────────── */}
                                {step === 'upload' && (
                                    <div className="p-6 space-y-6">
                                        {/* File drop zone */}
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                                Specification Document
                                            </label>
                                            <div
                                                role="button"
                                                tabIndex={0}
                                                onDrop={handleDrop}
                                                onDragOver={handleDragOver}
                                                onDragLeave={handleDragLeave}
                                                onClick={() => fileInputRef.current?.click()}
                                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
                                                className={`relative flex flex-col items-center justify-center gap-3 px-6 py-10 rounded-xl border-2 border-dashed cursor-pointer transition-colors duration-150 ${isDragging ? 'border-violet-400 bg-violet-50 dark:bg-violet-950/20' : file ? 'border-green-400 bg-green-50 dark:bg-green-950/20' : 'border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-gh-bg-subtle-dark hover:border-violet-400 hover:bg-violet-50 dark:hover:bg-violet-950/20'}`}
                                            >
                                                <input
                                                    ref={fileInputRef}
                                                    type="file"
                                                    accept={ACCEPTED_EXT}
                                                    className="hidden"
                                                    onChange={handleFileInput}
                                                />
                                                {file ? (
                                                    <>
                                                        <div className="flex items-center gap-2">
                                                            {getFileIcon(file.name)}
                                                            <span className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate max-w-xs">
                                                                {file.name}
                                                            </span>
                                                        </div>
                                                        <span className="text-xs text-slate-500 dark:text-slate-400">
                                                            {formatBytes(file.size)} — click to change
                                                        </span>
                                                        <CheckCircle2 size={20} className="text-green-500" />
                                                    </>
                                                ) : (
                                                    <>
                                                        <Upload size={28} className="text-slate-400 dark:text-slate-500" />
                                                        <div className="text-center">
                                                            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                                                Drop your spec here, or click to browse
                                                            </p>
                                                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                                                PDF, DOCX, or Markdown — up to 10 MB
                                                            </p>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                            {fileError && (
                                                <p className="mt-2 flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400">
                                                    <AlertCircle size={12} />
                                                    {fileError}
                                                </p>
                                            )}
                                        </div>

                                        {/* Test style selector */}
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                                                Test Style
                                            </label>
                                            <div className="grid grid-cols-3 gap-3">
                                                {STYLE_OPTIONS.map((opt) => (
                                                    <button
                                                        key={opt.value}
                                                        onClick={() => setTestStyle(opt.value)}
                                                        className={`flex flex-col items-start gap-1.5 p-3 rounded-xl border-2 text-left transition-all duration-150 ${testStyle === opt.value ? 'border-violet-500 bg-violet-50 dark:bg-violet-950/30' : 'border-slate-200 dark:border-gh-border-dark bg-white dark:bg-gh-bg-dark hover:border-violet-300 dark:hover:border-violet-700'}`}
                                                    >
                                                        <span className={`text-sm font-semibold ${testStyle === opt.value ? 'text-violet-700 dark:text-violet-300' : 'text-slate-800 dark:text-slate-200'}`}>
                                                            {opt.label}
                                                        </span>
                                                        <span className={`text-[10px] font-medium uppercase tracking-wider ${testStyle === opt.value ? 'text-violet-500 dark:text-violet-400' : 'text-slate-400 dark:text-slate-500'}`}>
                                                            {opt.description}
                                                        </span>
                                                        <p className="text-[10px] text-slate-500 dark:text-slate-400 leading-relaxed line-clamp-2">
                                                            {opt.example}
                                                        </p>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* ── STEP 2: Processing ─────────────────────── */}
                                {step === 'processing' && (
                                    <div className="p-8 space-y-8">
                                        <div className="text-center">
                                            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-violet-50 dark:bg-violet-950/40 mb-4">
                                                <Wand2 size={24} className="text-violet-600 dark:text-violet-400 animate-pulse" />
                                            </div>
                                            <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                                                AI is analysing your document
                                            </h3>
                                            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                                                This may take 30–90 seconds for large documents. Please wait…
                                            </p>
                                        </div>

                                        {pipelineError ? (
                                            <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800">
                                                <AlertTriangle size={18} className="text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
                                                <div>
                                                    <p className="text-sm font-medium text-red-700 dark:text-red-300">
                                                        Pipeline failed
                                                    </p>
                                                    <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">
                                                        {pipelineError}
                                                    </p>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="space-y-3">
                                                {PIPELINE_STAGES.map(({ stage, icon: Icon, label }) => {
                                                    const done = pipelineStages.find((s) => s.stage === stage);
                                                    const active = currentStage === stage && !done;
                                                    const pending = currentStage < stage;

                                                    return (
                                                        <div
                                                            key={stage}
                                                            className={`flex items-center gap-4 p-4 rounded-xl border transition-all duration-300 ${done ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/20' : active ? 'border-violet-300 dark:border-violet-700 bg-violet-50 dark:bg-violet-950/20' : 'border-slate-200 dark:border-gh-border-dark bg-white dark:bg-gh-bg-dark opacity-50'}`}
                                                        >
                                                            <div className={`flex items-center justify-center w-8 h-8 rounded-full shrink-0 ${done ? 'bg-green-100 dark:bg-green-900/40' : active ? 'bg-violet-100 dark:bg-violet-900/40' : 'bg-slate-100 dark:bg-slate-800'}`}>
                                                                {done ? (
                                                                    <CheckCircle2 size={16} className="text-green-600 dark:text-green-400" />
                                                                ) : active ? (
                                                                    <Loader2 size={16} className="text-violet-600 dark:text-violet-400 animate-spin" />
                                                                ) : (
                                                                    <Icon size={16} className="text-slate-400" />
                                                                )}
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <p className={`text-sm font-medium ${done ? 'text-green-700 dark:text-green-300' : active ? 'text-violet-700 dark:text-violet-300' : 'text-slate-500 dark:text-slate-400'}`}>
                                                                    {done?.label ?? label}
                                                                </p>
                                                            </div>
                                                            {done && done.count > 0 && (
                                                                <span className="shrink-0 text-xs font-medium text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/40 px-2 py-0.5 rounded-full">
                                                                    {done.count}
                                                                </span>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* ── STEP 3: Review ─────────────────────────── */}
                                {step === 'review' && result && (
                                    <div className="divide-y divide-slate-100 dark:divide-gh-border-dark">

                                        {/* Stats bar */}
                                        <div className="px-6 py-4 bg-violet-50 dark:bg-violet-950/20 flex flex-wrap items-center gap-4">
                                            <StatPill label="Generated" value={result.stats.approved} color="violet" />
                                            <StatPill label="Requirements" value={result.stats.extracted} color="blue" />
                                            {result.stats.duplicatesRemoved > 0 && (
                                                <StatPill label="Duplicates removed" value={result.stats.duplicatesRemoved} color="amber" />
                                            )}
                                            {result.dropped.length > 0 && (
                                                <StatPill label="Dropped" value={result.dropped.length} color="red" />
                                            )}
                                        </div>

                                        {/* Tests by suite */}
                                        <div className="px-6 py-4">
                                            <p className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-3">
                                                Generated Tests
                                            </p>
                                            {result.generated.length === 0 ? (
                                                <div className="text-center py-8 text-slate-400 dark:text-slate-500">
                                                    <ClipboardList size={32} strokeWidth={1.5} className="mx-auto mb-2" />
                                                    <p className="text-sm">No tests were generated. Try a different document or style.</p>
                                                </div>
                                            ) : (
                                                <div className="space-y-2">
                                                    {Object.entries(groupedTests()).map(([suite, tests]) => (
                                                        <div key={suite} className="rounded-xl border border-slate-200 dark:border-gh-border-dark overflow-hidden">
                                                            {/* Suite header */}
                                                            <button
                                                                onClick={() => toggleSuite(suite)}
                                                                className="w-full flex items-center gap-2 px-4 py-2.5 bg-slate-50 dark:bg-gh-bg-subtle-dark hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-colors text-left"
                                                            >
                                                                {expandedSuites.has(suite) ? <ChevronDown size={14} className="text-slate-400 shrink-0" /> : <ChevronRight size={14} className="text-slate-400 shrink-0" />}
                                                                <span className="flex-1 text-sm font-semibold text-slate-800 dark:text-slate-200 truncate">
                                                                    {suite}
                                                                </span>
                                                                <span className="shrink-0 text-xs text-slate-500 dark:text-slate-400 bg-slate-200 dark:bg-slate-700 rounded-full px-2 py-0.5">
                                                                    {tests.length}
                                                                </span>
                                                            </button>

                                                            {/* Suite tests */}
                                                            {expandedSuites.has(suite) && (
                                                                <div className="divide-y divide-slate-100 dark:divide-gh-border-dark">
                                                                    {tests.map((test, idx) => {
                                                                        const testKey = `${suite}::${idx}`;
                                                                        const isExpanded = expandedTests.has(testKey);
                                                                        return (
                                                                            <div key={testKey}>
                                                                                <button
                                                                                    onClick={() => toggleTest(testKey)}
                                                                                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors text-left"
                                                                                >
                                                                                    {isExpanded ? <ChevronDown size={12} className="text-slate-400 shrink-0" /> : <ChevronRight size={12} className="text-slate-400 shrink-0" />}
                                                                                    <span className="flex-1 text-sm text-slate-700 dark:text-slate-300 truncate">
                                                                                        {test.title}
                                                                                    </span>
                                                                                    <span className="shrink-0 text-xs text-slate-400 dark:text-slate-500">
                                                                                        {test.steps.length} step{test.steps.length !== 1 ? 's' : ''}
                                                                                    </span>
                                                                                </button>

                                                                                {/* Step preview */}
                                                                                {isExpanded && (
                                                                                    <div className="px-8 pb-3 space-y-1.5">
                                                                                        {test.preconditions && (
                                                                                            <p className="text-xs text-slate-500 dark:text-slate-400 italic mb-2">
                                                                                                Preconditions: {test.preconditions}
                                                                                            </p>
                                                                                        )}
                                                                                        {test.steps.map((step, si) => (
                                                                                            <div key={`${testKey}-step-${si}`} className="flex gap-3 text-xs">
                                                                                                <span className="shrink-0 text-slate-400 dark:text-slate-500 tabular-nums w-4 text-right">
                                                                                                    {si + 1}.
                                                                                                </span>
                                                                                                <div className="flex-1 min-w-0">
                                                                                                    <p className="text-slate-700 dark:text-slate-300">{step.action}</p>
                                                                                                    {step.expectedResult && (
                                                                                                        <p className="text-slate-500 dark:text-slate-400 mt-0.5">
                                                                                                            → {step.expectedResult}
                                                                                                        </p>
                                                                                                    )}
                                                                                                </div>
                                                                                            </div>
                                                                                        ))}
                                                                                        {Array.isArray(test.tags) && test.tags.length > 0 && (
                                                                                            <div className="flex flex-wrap gap-1 mt-2">
                                                                                                {test.tags.map((tag) => (
                                                                                                    <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
                                                                                                        {tag}
                                                                                                    </span>
                                                                                                ))}
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        {/* Dropped tests (collapsible) */}
                                        {result.dropped.length > 0 && (
                                            <div className="px-6 py-4">
                                                <button
                                                    onClick={() => setShowDropped((v) => !v)}
                                                    className="flex items-center gap-2 text-xs font-semibold text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 uppercase tracking-wider transition-colors"
                                                >
                                                    {showDropped ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                                    Filtered Out ({result.dropped.length})
                                                </button>
                                                {showDropped && (
                                                    <div className="mt-3 space-y-1.5">
                                                        {result.dropped.map((d) => (
                                                            <div key={d.title} className="flex items-start gap-2 p-2.5 rounded-lg bg-slate-50 dark:bg-gh-bg-subtle-dark border border-slate-200 dark:border-gh-border-dark">
                                                                <AlertTriangle size={12} className="text-amber-500 shrink-0 mt-0.5" />
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="text-xs font-medium text-slate-700 dark:text-slate-300 truncate">
                                                                        {d.title}
                                                                    </p>
                                                                    <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
                                                                        {d.reason}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {importError && (
                                            <div className="px-6 py-3">
                                                <p className="flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400">
                                                    <AlertCircle size={12} />
                                                    {importError}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* ── Modal Footer ──────────────────────────────── */}
                            <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-slate-200 dark:border-gh-border-dark bg-slate-50 dark:bg-gh-bg-subtle-dark shrink-0">
                                {step === 'upload' && (
                                    <>
                                        <button
                                            onClick={handleClose}
                                            className="px-4 py-2 text-sm font-medium rounded-lg border border-slate-300 dark:border-gh-border-dark text-slate-700 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-800 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={runPipeline}
                                            disabled={!file || !!fileError}
                                            className="flex items-center gap-2 px-5 py-2 text-sm font-medium rounded-lg bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            <Wand2 size={14} />
                                            Analyze Document
                                        </button>
                                    </>
                                )}

                                {step === 'processing' && (
                                    <>
                                        <span className="text-xs text-slate-500 dark:text-slate-400">
                                            {pipelineError ? 'Pipeline stopped.' : 'Running 4-stage AI pipeline…'}
                                        </span>
                                        {pipelineError && (
                                            <button
                                                onClick={() => setStep('upload')}
                                                className="px-4 py-2 text-sm font-medium rounded-lg border border-slate-300 dark:border-gh-border-dark text-slate-700 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-800 transition-colors"
                                            >
                                                ← Go Back
                                            </button>
                                        )}
                                    </>
                                )}

                                {step === 'review' && result && (
                                    <>
                                        <button
                                            onClick={() => setStep('upload')}
                                            disabled={isImporting}
                                            className="px-4 py-2 text-sm font-medium rounded-lg border border-slate-300 dark:border-gh-border-dark text-slate-700 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-800 disabled:opacity-50 transition-colors"
                                        >
                                            ← Upload Different File
                                        </button>
                                        <button
                                            onClick={handleImport}
                                            disabled={isImporting || result.generated.length === 0}
                                            className="flex items-center gap-2 px-5 py-2 text-sm font-medium rounded-lg bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            {isImporting
                                                ? <><Loader2 size={14} className="animate-spin" /> Importing…</>
                                                : <><Download size={14} /> Import {result.generated.length} Test{result.generated.length !== 1 ? 's' : ''}</>}
                                        </button>
                                    </>
                                )}
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </Dialog>
        </Transition>
    );
}

// ── Stat Pill ─────────────────────────────────────────────────────────────────

function StatPill({ label, value, color }: { label: string; value: number; color: 'violet' | 'blue' | 'amber' | 'red' }) {
    const colors = {
        violet: 'bg-violet-100 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300',
        blue: 'bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300',
        amber: 'bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300',
        red: 'bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-300',
    };
    return (
        <div className="flex items-center gap-1.5">
            <span className={`text-sm font-bold ${colors[color]} px-2 py-0.5 rounded-full`}>{value}</span>
            <span className="text-xs text-slate-600 dark:text-slate-400">{label}</span>
        </div>
    );
}
